import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ConfirmBoxComponent } from '../../../components/confirm-box/confirm-box.component';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { smallModalConfig } from '../../../consts/modal-config.const';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { ToolbarService } from '../../../services/toolbar.service';
import { UserService } from '../../../services/user.service';
import { JobViewerStore } from '../job-viewer.store';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';

@Component({
  selector: 'app-add-return-no-quote',
  templateUrl: './add-return-no-quote.component.html',
  styleUrls: ['./add-return-no-quote.component.scss'],
  imports: [
    MatCard,
    MatIconButton,
    MatIcon,
    MatCardContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatError,
    MatInput,
    CdkTextareaAutosize,
    BrowserAutoFillDisabledDirective
  ]
})
export class AddReturnNoQuoteComponent implements OnInit, AfterViewInit {
  @Input() jobId: number;
  @Input() assessmentAction: string;
  @Input() assessorAllocated: boolean;
  @Input() hasQuotes: boolean;
  @Input() displayRFA: boolean;
  @Input() displaySupplementRFA: boolean;
  @Input() note: string;
  @Output() refreshJobViewer: EventEmitter<any> = new EventEmitter();

  replyForm: UntypedFormGroup;

  toolbarService = inject(ToolbarService);
  #jobViewerStore = inject(JobViewerStore);
  #userService = inject(UserService);
  #confirmBox = inject(MatDialog);
  #message = inject(MessagingService);
  #elRef = inject(ElementRef);
  #fb = inject(UntypedFormBuilder);
  #modalConfigsHelper = inject(ModalConfigsHelper);

  constructor() {
    effect(() => {
      if (this.#jobViewerStore.noQuoteResponseSaved() && this.assessmentAction) {
        this.updateJob('noQuoteResponseAssessmentAction');
        this.#jobViewerStore.noQuoteResponseSaved.set(false);
      }
    });
  }

  get noQuoteResponseAssessmentAction() {
    return this.replyForm.get('noQuoteResponseAssessmentAction');
  }
  get noQuoteResponseNote() {
    return this.replyForm.get('noQuoteResponseNote');
  }

  ngOnInit(): void {
    this.replyForm = this.#fb.group({
      noQuoteResponseAssessmentAction: [this.assessmentAction, [Validators.required]],
      noQuoteResponseNote: ['']
    });

    this.populateReplyForm();

    this.noQuoteResponseNote.valueChanges.pipe(debounceTime(750), distinctUntilChanged()).subscribe(() => {
      if (this.noQuoteResponseNote.dirty) {
        this.updateJob('noQuoteResponseNote');
      }
    });
  }

  ngAfterViewInit(): void {
    this.#elRef.nativeElement.scrollIntoView({
      behavior: 'smooth'
    });
  }

  populateReplyForm() {
    this.replyForm.get('noQuoteResponseAssessmentAction').setValue(this.assessmentAction);
    this.replyForm.get('noQuoteResponseNote').setValue(this.note);
  }

  updateJob(fieldName: string) {
    this.toolbarService.setCloudState('SAVING');
    this.#userService.updateJob(this.replyForm.get(fieldName).value, this.jobId, fieldName).subscribe(
      () => this.toolbarService.setCloudState('RESTING'),
      () => {
        this.#message.error('Could not update field.');
        this.toolbarService.setCloudState('RESTING');
      },
      () => {
        this.refreshJobViewer.emit(this.replyForm.get(fieldName).value === 'Total Loss' ? null : 'isNotTotalLossCase');
        this.replyForm.controls[fieldName].markAsPristine();
      }
    );
  }

  confirmDeleteReplyCard() {
    const data = { title: 'Delete Reply', alertMessage: 'This reply will be deleted.', confirmButton: 'DELETE' };
    const dialogRef = this.#confirmBox.open(
      ConfirmBoxComponent,
      this.#modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig)
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'DELETE') {
        this.deleteReply();
      }
    });
  }

  deleteReply() {
    this.#userService.updateJob('false', this.jobId, 'noQuoteResponse').subscribe(
      () => this.toolbarService.setCloudState('RESTING'),
      () => {
        this.#message.error('Could not update field.');
        this.toolbarService.setCloudState('RESTING');
      },
      () => this.refreshJobViewer.emit('DeleteReturnToAssessor')
    );
  }
}
